/**
 * Cliente de autenticación de navegador (docs/FASE-7.md §6): el patrón de SPA **sin backend** que
 * `INTEGRACION-MODULO.md` §e dejaba pendiente.
 *
 * La decisión que explica todo lo demás: **los tokens no se guardan en ningún sitio**. Ni el access
 * token ni el refresh token salen de memoria, así que al recargar la página no queda nada que
 * robar... y nada que reutilizar. Lo que reemplaza a un token persistido es la sesión de dominio del
 * IdP (cookie `cnt_sso`, `httpOnly`): al arrancar, el cliente pide autorización con `prompt=none` y
 * vuelve con un código nuevo sin que se pinte una sola pantalla.
 *
 * Y la segunda, que va con la primera: **la autenticación silenciosa es una navegación de primer
 * nivel, nunca un iframe oculto**. El IdP no se deja empotrar (`frame-ancestors 'self'`), y además un
 * iframe no llevaría la cookie en un módulo con dominio propio, donde la petición sería cross-site.
 * El coste es una redirección al arrancar; el beneficio es que funciona igual en todos los módulos.
 *
 * Todo lo externo se inyecta (`fetch`, `crypto`, almacenamiento, navegación), así que el archivo
 * entero se prueba con `node --test` sin navegador.
 */
import { createCodeChallenge, createCodeVerifier, randomUrlSafe } from "./pkce.js";
import { clearSilentTried, createMemoryStorage, markSilentTried, saveTransaction, takeTransaction, wasSilentTried, } from "./storage.js";
/** Margen de renovación: se pide un token nuevo 60 s antes de que expire, igual que `platform-client`. */
const RENEW_MARGIN_MS = 60_000;
const DEFAULT_SCOPE = "openid profile email offline_access";
export function createAuthClient(options) {
    const issuer = options.issuer.replace(/\/+$/, "");
    const scope = options.scope ?? DEFAULT_SCOPE;
    const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    const cryptoImpl = options.crypto ?? globalThis.crypto;
    const storage = options.storage ?? resolveSessionStorage();
    const now = options.now ?? Date.now;
    const navigate = options.navigate ?? ((url) => globalThis.location?.assign(url));
    const currentUrl = options.currentUrl ?? (() => globalThis.location?.href ?? "/");
    const replaceUrl = options.replaceUrl ?? ((url) => globalThis.history?.replaceState(null, "", url));
    let tokens = null;
    let state = { status: "loading", claims: null, orgId: null, orgSlug: null, error: null };
    const listeners = new Set();
    let refreshInFlight = null;
    function emit(next) {
        state = next;
        for (const listener of listeners)
            listener(state);
        return state;
    }
    function anonymous(error) {
        tokens = null;
        return emit({ status: "anonymous", claims: null, orgId: null, orgSlug: null, error });
    }
    function authenticated(next) {
        tokens = next;
        const claims = decodeClaims(next.accessToken);
        clearSilentTried(storage);
        return emit({
            status: "authenticated",
            claims,
            orgId: claims?.org ?? null,
            orgSlug: claims?.org_slug ?? null,
            error: null,
        });
    }
    async function startAuthorize(silent, orgHint) {
        if (!cryptoImpl) {
            anonymous("Este navegador no expone Web Crypto: no se puede iniciar sesión.");
            return;
        }
        const codeVerifier = createCodeVerifier(cryptoImpl);
        const challenge = await createCodeChallenge(cryptoImpl, codeVerifier);
        const txState = randomUrlSafe(cryptoImpl, 16);
        saveTransaction(storage, {
            state: txState,
            codeVerifier,
            returnTo: stripOAuthParams(currentUrl()),
            createdAt: now(),
            silent,
        });
        if (silent)
            markSilentTried(storage);
        const url = new URL(`${issuer}/oauth/authorize`);
        url.searchParams.set("client_id", options.clientId);
        url.searchParams.set("redirect_uri", options.redirectUri);
        url.searchParams.set("response_type", "code");
        url.searchParams.set("scope", scope);
        url.searchParams.set("state", txState);
        url.searchParams.set("code_challenge", challenge);
        url.searchParams.set("code_challenge_method", "S256");
        const hint = orgHint ?? options.orgHint ?? null;
        if (hint)
            url.searchParams.set("org_hint", hint);
        if (silent)
            url.searchParams.set("prompt", "none");
        navigate(url.toString());
    }
    async function exchangeCode(code, codeVerifier) {
        const res = await doFetch(`${issuer}/oauth/token`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
                grant_type: "authorization_code",
                code,
                redirect_uri: options.redirectUri,
                code_verifier: codeVerifier,
                client_id: options.clientId,
            }),
        });
        if (!res.ok)
            return anonymous(`No se pudo canjear el código de autorización (${String(res.status)}).`);
        return authenticated(toTokens((await res.json()), now()));
    }
    async function refresh() {
        const refreshToken = tokens?.refreshToken;
        if (!refreshToken)
            return null;
        const res = await doFetch(`${issuer}/oauth/token`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ grant_type: "refresh_token", refresh_token: refreshToken, client_id: options.clientId }),
        });
        if (!res.ok) {
            anonymous("La sesión expiró.");
            return null;
        }
        // Cada canje rota el refresh token: quedarse con el viejo haría que el siguiente intento
        // pareciera un robo (el núcleo revoca la sesión ante un refresh reusado).
        const next = authenticated(toTokens((await res.json()), now()));
        return next.status === "authenticated" ? (tokens?.accessToken ?? null) : null;
    }
    return {
        async bootstrap() {
            const url = new URL(currentUrl(), "http://localhost");
            const code = url.searchParams.get("code");
            const returnedState = url.searchParams.get("state");
            const error = url.searchParams.get("error");
            if (code !== null || error !== null) {
                const tx = takeTransaction(storage, now());
                if (!tx || returnedState === null || tx.state !== returnedState) {
                    // `state` que no coincide: o es una respuesta vieja, o alguien la fabricó. En los dos
                    // casos se descarta sin canjear nada.
                    replaceUrl(safeReturnTo(tx?.returnTo));
                    return anonymous("La respuesta de autorización no corresponde a esta pestaña.");
                }
                replaceUrl(safeReturnTo(tx.returnTo));
                if (error !== null) {
                    // `login_required` no es un fallo: es «no hay sesión». Se distingue para que la interfaz
                    // muestre el botón de entrar en vez de un mensaje de error.
                    return anonymous(error === "login_required" ? null : error);
                }
                return exchangeCode(code, tx.codeVerifier);
            }
            if (options.silentOnBootstrap === false || wasSilentTried(storage)) {
                return anonymous(null);
            }
            await startAuthorize(true, null);
            // La navegación se lleva la página por delante; este estado solo se ve en tests o si el
            // navegador ignora la redirección.
            return state;
        },
        getState: () => state,
        getAccessToken: () => tokens?.accessToken ?? null,
        async getFreshAccessToken() {
            if (!tokens)
                return null;
            if (tokens.expiresAtMs - RENEW_MARGIN_MS > now())
                return tokens.accessToken;
            refreshInFlight ??= refresh().finally(() => {
                refreshInFlight = null;
            });
            return refreshInFlight;
        },
        async signIn(signInOptions) {
            clearSilentTried(storage);
            await startAuthorize(false, signInOptions?.orgHint ?? null);
        },
        async switchOrg(orgId) {
            const refreshToken = tokens?.refreshToken;
            if (!refreshToken)
                return anonymous("No hay sesión que cambiar de organización.");
            const res = await doFetch(`${issuer}/oauth/switch-org`, {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ refresh_token: refreshToken, org_id: orgId, client_id: options.clientId }),
            });
            if (!res.ok)
                return anonymous(`No se pudo cambiar de organización (${String(res.status)}).`);
            return authenticated(toTokens((await res.json()), now()));
        },
        signOut() {
            tokens = null;
            emit({ status: "anonymous", claims: null, orgId: null, orgSlug: null, error: null });
            const url = new URL(`${issuer}/oauth/logout`);
            if (options.postLogoutRedirectUri) {
                url.searchParams.set("post_logout_redirect_uri", options.postLogoutRedirectUri);
            }
            navigate(url.toString());
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}
function toTokens(response, nowMs) {
    return {
        accessToken: response.access_token,
        refreshToken: response.refresh_token ?? null,
        expiresAtMs: nowMs + response.expires_in * 1000,
    };
}
/**
 * Lee los claims del access token **sin verificar la firma**, que es lo correcto aquí: el navegador
 * no valida tokens, solo los enseña. Quien decide si un token vale es el backend del módulo con
 * `@continnua/auth-server`.
 */
export function decodeClaims(accessToken) {
    const payload = accessToken.split(".")[1];
    if (payload === undefined)
        return null;
    try {
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        // `atob` devuelve bytes como caracteres latin-1; el paso por `%xx` los reinterpreta como UTF-8,
        // que es lo que hace que un nombre con tilde dentro del token no salga hecho un galimatías.
        const json = decodeURIComponent(atob(base64)
            .split("")
            .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join(""));
        const parsed = JSON.parse(json);
        return {
            sub: String(parsed.sub ?? ""),
            sid: String(parsed.sid ?? ""),
            aud: String(parsed.aud ?? ""),
            org: typeof parsed.org === "string" ? parsed.org : null,
            org_slug: typeof parsed.org_slug === "string" ? parsed.org_slug : null,
            roles: Array.isArray(parsed.roles) ? parsed.roles.map(String) : [],
            exp: typeof parsed.exp === "number" ? parsed.exp : 0,
            scope: typeof parsed.scope === "string" ? parsed.scope : "",
        };
    }
    catch {
        return null;
    }
}
/**
 * Normaliza el `returnTo` leído del almacenamiento antes de usarlo.
 *
 * Lo que se guardó ya era una ruta relativa, pero se vuelve a pasar por `stripOAuthParams` al
 * leerlo: el contenido de `sessionStorage` es entrada, no un valor de confianza, y de aquí sale
 * directo a `history.replaceState`. Así nunca puede acabar en la barra de direcciones un origen
 * ajeno, ni volver un `code` que ya se canjeó.
 */
function safeReturnTo(returnTo) {
    return stripOAuthParams(returnTo ?? "/");
}
/** Quita los parámetros del flujo OAuth de una URL, para no dejarlos en la barra de direcciones. */
export function stripOAuthParams(rawUrl) {
    try {
        const url = new URL(rawUrl, "http://localhost");
        for (const param of ["code", "state", "error", "error_description", "session_state", "iss"]) {
            url.searchParams.delete(param);
        }
        return `${url.pathname}${url.search}${url.hash}`;
    }
    catch {
        return "/";
    }
}
function resolveSessionStorage() {
    try {
        const candidate = globalThis.sessionStorage;
        if (candidate) {
            // Una pestaña privada muy estricta deja el objeto en su sitio y lanza al escribir: se
            // comprueba de verdad antes de confiar en él.
            candidate.setItem("cnt:auth:probe", "1");
            candidate.removeItem("cnt:auth:probe");
            return candidate;
        }
    }
    catch {
        // Cae al almacenamiento en memoria.
    }
    return createMemoryStorage();
}
//# sourceMappingURL=client.js.map